"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type AdMedium,
  type AdvertisementLabels,
  advertisementIsReady,
  ADVERTISEMENT_FIELDS as F,
  formatAdvertisement,
} from "@/lib/tool-kit/media-sources/advertisement-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const ADVERTISEMENT_CITATION_GENERATOR_STRINGS = {
  formHeading: "Advertisement details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the advertiser or the ad's title, and the date it ran, to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  mediumLabel: "Where did the ad appear?",
  mediumPrint: "Print",
  mediumBroadcast: "TV or radio",
  mediumOnline: "Online",
  mediumOutdoor: "Outdoor",
  advertiserLabel: "Advertiser (brand or company)",
  advertiserPlaceholder: "Patagonia",
  advertiserHint:
    "APA and Chicago start with the advertiser. MLA starts with the ad's title or a description.",
  productLabel: "Product advertised (optional)",
  productPlaceholder: "Head and Shoulders",
  productHint:
    "Name it as you would in a sentence, brand included: GE refrigerator. It describes an untitled ad: Advertisement for Head and Shoulders.",
  productIsTitleLabel: "The product is a titled work, such as a film or show",
  productIsTitleHint: "Its title is then set in italics.",
  titleLabel: "Title, headline or tagline (optional)",
  titlePlaceholder: "Don’t buy this jacket",
  titleHint:
    "Type it in sentence case. Leave it blank if the ad has no headline; the generator describes it instead.",
  agencyLabel: "Agency or producer (optional, Chicago)",
  agencyPlaceholder: "Chiat/Day",
  publicationLabel: "Newspaper or magazine",
  publicationPlaceholder: "The New York Times",
  volumeLabel: "Volume (magazines)",
  volumePlaceholder: "169",
  issueLabel: "Issue",
  issuePlaceholder: "3",
  pageLabel: "Page",
  pagePlaceholder: "A11",
  broadcastMediumLabel: "Medium",
  broadcastTelevision: "Television",
  broadcastRadio: "Radio",
  networkLabel: "Network or station",
  networkPlaceholder: "CBS",
  programLabel: "Program it aired during (optional)",
  programPlaceholder: "Super Bowl XVIII",
  websiteLabel: "Website or app",
  websitePlaceholder: "The New Yorker",
  adFormatLabel: "Kind of online ad (optional)",
  adFormatPlaceholder: "Pop-up ad",
  urlLabel: "URL",
  urlPlaceholder: "https://www.newyorker.com",
  outdoorMediumLabel: "Kind of outdoor ad",
  outdoorBillboard: "Billboard",
  outdoorPoster: "Poster",
  outdoorTransit: "Transit (bus, subway)",
  locationLabel: "Where you saw it",
  locationPlaceholder: "Times Square",
  cityLabel: "City",
  cityPlaceholder: "New York City",
  regionLabel: "State or province (APA)",
  regionPlaceholder: "NY",
  datePrintLabel: "Date of the issue",
  dateBroadcastLabel: "Date it aired",
  dateOnlineLabel: "Date posted or seen",
  dateOutdoorLabel: "Date you saw it",
  datePlaceholder: "YYYY-MM-DD",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 has no advertisement example. This applies its general pattern: the advertiser as author, the date, the title or a description in brackets, and where the ad ran.",
  mlaNote:
    "MLA 9 starts with the ad's title in quotation marks, or with a description such as Advertisement for …, then where and when it appeared. A billboard is dated by the day you saw it.",
  chicagoNote:
    "The Chicago Manual has no rule for print ads. This follows the form the Chicago Style Q&A gives for a television ad; a well-known ad can also simply be described in your text.",
  examplePrint: {
    advertiser: "Patagonia",
    title: "Don’t buy this jacket",
    publication: "The New York Times",
    date: "2011-11-25",
  },
  exampleBroadcast: {
    advertiser: "Apple Computer",
    title: "1984",
    agency: "Chiat/Day",
    broadcastMedium: "television",
    network: "CBS",
    program: "Super Bowl XVIII",
    date: "1984-01-22",
  },
  exampleOnline: {
    advertiser: "The New Yorker",
    title: "Get the best of The New Yorker",
    website: "The New Yorker",
    adFormat: "Pop-up ad",
    date: "2017-01-10",
    url: "https://www.newyorker.com",
  },
  exampleOutdoor: {
    product: "School of Rock: The Musical",
    productIsTitle: "true",
    outdoorMedium: "billboard",
    location: "Times Square",
    city: "New York City",
    region: "NY",
    date: "2017-01-29",
  },
} as const;

type Strings = typeof ADVERTISEMENT_CITATION_GENERATOR_STRINGS;

function adFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.advertiser,
      kind: "text",
      label: strings.advertiserLabel,
      placeholder: strings.advertiserPlaceholder,
      hint: strings.advertiserHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.product,
      kind: "text",
      label: strings.productLabel,
      placeholder: strings.productPlaceholder,
      hint: strings.productHint,
    },
    {
      id: F.productIsTitle,
      kind: "checkbox",
      label: strings.productIsTitleLabel,
      hint: strings.productIsTitleHint,
    },
  ];
}

function dateField(label: string, strings: Strings): SourceField {
  return {
    id: F.date,
    kind: "date",
    label,
    placeholder: strings.datePlaceholder,
  };
}

function printFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.publication,
      kind: "text",
      label: strings.publicationLabel,
      placeholder: strings.publicationPlaceholder,
    },
    dateField(strings.datePrintLabel, strings),
    {
      id: F.volume,
      kind: "text",
      label: strings.volumeLabel,
      placeholder: strings.volumePlaceholder,
    },
    {
      id: F.issue,
      kind: "text",
      label: strings.issueLabel,
      placeholder: strings.issuePlaceholder,
    },
    {
      id: F.page,
      kind: "text",
      label: strings.pageLabel,
      placeholder: strings.pagePlaceholder,
    },
  ];
}

function broadcastFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.broadcastMedium,
      kind: "select",
      label: strings.broadcastMediumLabel,
      options: [
        { value: "television", label: strings.broadcastTelevision },
        { value: "radio", label: strings.broadcastRadio },
      ],
    },
    {
      id: F.network,
      kind: "text",
      label: strings.networkLabel,
      placeholder: strings.networkPlaceholder,
    },
    {
      id: F.program,
      kind: "text",
      label: strings.programLabel,
      placeholder: strings.programPlaceholder,
    },
    dateField(strings.dateBroadcastLabel, strings),
    {
      id: F.agency,
      kind: "text",
      label: strings.agencyLabel,
      placeholder: strings.agencyPlaceholder,
    },
  ];
}

function onlineFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.website,
      kind: "text",
      label: strings.websiteLabel,
      placeholder: strings.websitePlaceholder,
    },
    {
      id: F.adFormat,
      kind: "text",
      label: strings.adFormatLabel,
      placeholder: strings.adFormatPlaceholder,
    },
    dateField(strings.dateOnlineLabel, strings),
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

function outdoorFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.outdoorMedium,
      kind: "select",
      label: strings.outdoorMediumLabel,
      options: [
        { value: "billboard", label: strings.outdoorBillboard },
        { value: "poster", label: strings.outdoorPoster },
        { value: "transit", label: strings.outdoorTransit },
      ],
    },
    {
      id: F.location,
      kind: "text",
      label: strings.locationLabel,
      placeholder: strings.locationPlaceholder,
    },
    {
      id: F.city,
      kind: "text",
      label: strings.cityLabel,
      placeholder: strings.cityPlaceholder,
    },
    {
      id: F.region,
      kind: "text",
      label: strings.regionLabel,
      placeholder: strings.regionPlaceholder,
    },
    dateField(strings.dateOutdoorLabel, strings),
  ];
}

const MEDIUM_FIELDS: Record<AdMedium, (strings: Strings) => SourceField[]> = {
  print: printFields,
  broadcast: broadcastFields,
  online: onlineFields,
  outdoor: outdoorFields,
};

const EXAMPLE_KEYS = {
  print: "examplePrint",
  broadcast: "exampleBroadcast",
  online: "exampleOnline",
  outdoor: "exampleOutdoor",
} as const;

/** The widget's strings, as the formatter's labels. */
export function advertisementLabels(strings: Strings): AdvertisementLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function AdvertisementCitationGeneratorWidget() {
  const strings = useToolUi(ADVERTISEMENT_CITATION_GENERATOR_STRINGS);
  const [medium, setMedium] = useState<AdMedium>("print");
  const fields = useMemo(
    () => [...adFields(strings), ...MEDIUM_FIELDS[medium](strings)],
    [medium, strings]
  );
  const labels = useMemo(() => advertisementLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatAdvertisement(medium, values, labels),
    [medium, labels]
  );

  return (
    <SourceCitationBuilder
      example={strings[EXAMPLE_KEYS[medium]]}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.mediumLabel}
          name="advertisement-medium"
          onChange={setMedium}
          options={[
            { value: "print", label: strings.mediumPrint },
            { value: "broadcast", label: strings.mediumBroadcast },
            { value: "online", label: strings.mediumOnline },
            { value: "outdoor", label: strings.mediumOutdoor },
          ]}
          value={medium}
        />
      }
      isReady={advertisementIsReady}
      strings={strings}
    />
  );
}
